import { useCreate } from '@refinedev/core'
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Col,
  Typography
} from 'antd'
import {
  DownloadOutlined,
  ExclamationCircleFilled,
  KeyOutlined
} from '@ant-design/icons'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PageHeader, PageShell } from '@/components/page-shell'
import { useMessage } from '@/providers/message-provider'

const { Text, Paragraph } = Typography

/**
 * 新建项目页（F3）
 *
 * 表单字段：code / name / description / itemUrl / defaultSku /
 *           maxUnbind / offlineGraceDays / minClientVersion
 *
 * 提交后：
 *   1. 后端生成 Ed25519 密钥对
 *   2. 私钥仅此一次返回 → 弹窗展示 + 下载按钮（.pem 文件）
 *   3. 警告：丢失需轮换密钥对，所有客户端需重新激活
 */

const { TextArea } = Input

interface CreateProjectInput {
  code: string
  name: string
  description?: string
  itemUrl?: string
  defaultSku: string
  maxUnbind: number
  offlineGraceDays: number
  minClientVersion?: string
}

interface CreateProjectResponse {
  project: {
    id: string
    code: string
    name: string
  }
  privateKeyB64: string
  publicKeyB64: string
  publicKeyFingerprint: string
  warning: string
}

export default function ProjectCreatePage() {
  const [form] = Form.useForm<CreateProjectInput>()
  const navigate = useNavigate()
  const message = useMessage()
  const { mutate: createProject, isLoading } = useCreate<CreateProjectResponse>()

  // 私钥下载弹窗
  const [keyModalOpen, setKeyModalOpen] = useState(false)
  const [keyData, setKeyData] = useState<CreateProjectResponse | null>(null)

  const handleSubmit = (values: CreateProjectInput) => {
    createProject(
      {
        resource: 'projects',
        values
      },
      {
        onSuccess: ({ data }) => {
          setKeyData(data as unknown as CreateProjectResponse)
          setKeyModalOpen(true)
          message.success('项目创建成功！请立即下载私钥')
        },
        onError: (err) => {
          message.error((err as unknown as Error)?.message ?? '创建失败')
        }
      }
    )
  }

  /** 下载私钥为 .pem 文件 */
  const downloadPrivateKey = () => {
    if (!keyData) return
    const pemContent = `-----BEGIN PRIVATE KEY-----
${keyData.privateKeyB64}
-----END PRIVATE KEY-----`
    const blob = new Blob([pemContent], { type: 'application/x-pem-file' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${keyData.project.code}-private-key.pem`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    message.success('私钥已下载')
  }

  /** 下载公钥 */
  const downloadPublicKey = () => {
    if (!keyData) return
    const blob = new Blob([keyData.publicKeyB64], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${keyData.project.code}-public-key.b64`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /** 关闭弹窗 → 跳转项目详情 */
  const handleCloseKeyModal = () => {
    if (!keyData) return
    Modal.confirm({
      title: '确认已保存私钥？',
      icon: <ExclamationCircleFilled />,
      content: '私钥关闭后无法再次获取。如丢失需轮换密钥对，所有已激活客户端将需要重新激活。',
      okText: '已保存，前往项目详情',
      cancelText: '返回保存',
      onOk: () => {
        setKeyModalOpen(false)
        navigate(`/projects/${keyData.project.code}`)
      }
    })
  }

  return (
    <PageShell width="narrow">
      <PageHeader
        title="新建项目"
        subtitle="上架新的付费软件项目，后端会自动生成 Ed25519 密钥对与默认运行参数。"
        onBack={() => navigate('/projects')}
      />

      <Alert
        type="warning"
        showIcon
        icon={<KeyOutlined />}
        message="私钥仅此一次返回"
        description="项目创建成功后，私钥会以弹窗形式展示一次。请立即下载并妥善保管 .pem 文件。丢失需轮换密钥对，所有已激活客户端将需要重新激活。"
        style={{ marginBottom: 24 }}
      />

      <Card className="app-surface-card">
        <Form<CreateProjectInput>
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          initialValues={{
            defaultSku: 'lifetime-1device',
            maxUnbind: 3,
            offlineGraceDays: 7
          }}
        >
          <Form.Item
            name="code"
            label="项目 code"
            rules={[
              { required: true, message: '请输入项目 code' },
              {
                pattern: /^[2-9A-HJ-NP-Z]{3,16}$/,
                message:
                  '3-16 字符，大写字母+数字，不含 0/O/1/I/L（Crockford Base32，避免易混字符）'
              }
            ]}
            extra="创建后不可修改。用作序列号前缀和客户端识别。例：AUTRDESK"
          >
            <Input placeholder="AUTRDESK" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
          </Form.Item>

          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="自动回复助手" />
          </Form.Item>

          <Form.Item name="description" label="项目描述">
            <TextArea
              rows={3}
              placeholder="项目简介，便于后续维护识别"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item name="itemUrl" label="商品页 URL">
            <Input placeholder="https://www.goofish.com/item?id=xxx" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="defaultSku"
                label="默认 SKU"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: 'lifetime-1device', label: 'lifetime-1device（终身 1 设备）' },
                    { value: '1y-1device', label: '1y-1device（1 年 1 设备）' },
                    { value: '1y-2device', label: '1y-2device（1 年 2 设备）' },
                    { value: 'lifetime-3device', label: 'lifetime-3device（终身 3 设备）' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="maxUnbind"
                label="年度解绑上限"
                rules={[{ required: true }]}
                extra="每年允许换机次数"
              >
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="offlineGraceDays"
                label="离线宽限天数"
                rules={[{ required: true }]}
                extra="无网络时允许启动的天数"
              >
                <InputNumber min={0} max={90} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="minClientVersion" label="最低客户端版本（可选）">
                <Input placeholder="1.0.0" />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px solid var(--ant-color-border-secondary, #F1F5F9)'
            }}
          >
            <Button onClick={() => navigate('/projects')}>取消</Button>
            <Button type="primary" htmlType="submit" loading={isLoading} style={{ fontWeight: 500 }}>
              创建项目
            </Button>
          </div>
        </Form>
      </Card>

      {/* 私钥下载弹窗 */}
      <Modal
        open={keyModalOpen}
        title={
          <Space>
            <KeyOutlined style={{ color: '#F59E0B' }} />
            <span>项目私钥 — 请立即下载保存</span>
          </Space>
        }
        width={640}
        closable={false}
        maskClosable={false}
        footer={[
          <Button key="pub" icon={<DownloadOutlined />} onClick={downloadPublicKey}>
            下载公钥
          </Button>,
          <Button
            key="priv"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadPrivateKey}
          >
            下载私钥 .pem
          </Button>,
          <Button key="close" type="primary" danger onClick={handleCloseKeyModal}>
            已保存，前往项目
          </Button>
        ]}
      >
        {keyData && (
          <div>
            <Alert
              type="warning"
              showIcon
              message={keyData.warning}
              style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16 }}>
              <Text strong>项目：</Text>
              <Text>{keyData.project.name}（{keyData.project.code}）</Text>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                公钥指纹：
              </Text>
              <code
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 13,
                  padding: '4px 8px',
                  background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                  borderRadius: 4
                }}
              >
                {keyData.publicKeyFingerprint}
              </code>
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                私钥（Base64，仅展示此次）：
              </Text>
              <pre
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 11,
                  padding: 12,
                  background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                  borderRadius: 6,
                  maxHeight: 160,
                  overflow: 'auto',
                  margin: 0,
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {keyData.privateKeyB64}
              </pre>
            </div>

            <Paragraph
              type="secondary"
              style={{ marginTop: 16, fontSize: 12, marginBottom: 0 }}
            >
              <strong>用途：</strong> 私钥用于服务端签名 License payload；公钥用于客户端验签。
              客户端编译期注入公钥，私钥永不离开服务器。
            </Paragraph>
          </div>
        )}
      </Modal>
    </PageShell>
  )
}
